import { RELATED_AS_LABELS, RelatedAs } from "../../../../api/stakeholder_relationships";
import { AccountRelations, buildPartyTypeOptions } from "../../../../api/creditAccounts";
import FormField, { useTouched } from "./FormField";
import MultiSelectSummaryDropdown from "../../../../components/ui/selectdropdown/v2/MultiSelectSummaryDropdown";
import { OptionNameValuePair } from "../../../../lib/interfaces/types";
import { Trash2, X } from "lucide-react";
import ButtonV2 from "../../../../components/ui/button/v2";



const PARTY_TYPE_OPTIONS = buildPartyTypeOptions()

export interface RelationConnectorSvgProps {
  isFirst: boolean;
  hasParentError?: boolean;
}

export const RelationConnectorSvg: React.FC<RelationConnectorSvgProps> = ({
  isFirst,
  hasParentError = false,
}) => {
  const startY = hasParentError ? -35.5 : -17.5;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="21"
      height={isFirst ? "41" : "20"}
      viewBox={isFirst ? "0 0 21 41" : "0 0 21 20"}
      fill="none"
      className="absolute left-[-20px] top-0 pointer-events-none overflow-visible"
    >
      <path
        d={
          isFirst
            ? `M 0.5 ${startY} V 33.5 C 0.5 36.814 3.186 39.5 6.5 39.5 H 20.5`
            : "M 0.5 -12 V 12 C 0.5 15.314 3.186 18 6.5 18 H 20.5"
        }
        stroke="#E5E5E5"
        strokeWidth="1"
      />
    </svg>
  );
};

export interface PartyRelationRowProps {
    relations: AccountRelations;
    allRelations: AccountRelations[];
    accountOptions: OptionNameValuePair<string>[];
    onChange: (patch: Partial<AccountRelations>) => void;
    onDelete: () => void;
    showErrors: boolean;
    relationError?: string;
    accountNumbersError?: string;
    isFirst: boolean;
    isLast: boolean;
    parentHasError: boolean;
}

export const PartyRelationRow : React.FC<PartyRelationRowProps> = ({
    relations,
    allRelations,
    showErrors,
    relationError,
    accountNumbersError,
    isFirst,
    isLast,
    parentHasError,
    onChange,
    accountOptions,
    onDelete
}) => {
    const { isTouched, markTouched } = useTouched();
    const show = (key: string) => showErrors || isTouched(key);

    const isPrimaryBorrowerRow = relations.related_as === RelatedAs.PRIMARY_BORROWER;

    const rowPartyTypeOptions = isPrimaryBorrowerRow
    ? [
        {
            name: RELATED_AS_LABELS[RelatedAs.PRIMARY_BORROWER],
            value: RelatedAs.PRIMARY_BORROWER,
        },
        ...PARTY_TYPE_OPTIONS,
        ]
    : PARTY_TYPE_OPTIONS;

    const duplicateAccounts = relations.related_as ? 
        allRelations
        .filter((other) => other.id !== relations.id && other.related_as === relations.related_as)
        .flatMap((other) => other.account_numbers)
        .filter((accountNum) => relations.account_numbers.includes(accountNum))
    : [];

    const duplicateError = duplicateAccounts.length > 0 && relations.related_as
        ? `Account already added for ${RELATED_AS_LABELS[relations.related_as] || relations.related_as}`
        : undefined;

    const loanError = showErrors && accountNumbersError
        ? accountNumbersError
        : duplicateError;

    return (
        <div className={`relative w-full ${isPrimaryBorrowerRow ? "group" : ""}`}>
            {!isLast && (
                <div className="absolute left-[-20px] top-0 bottom-[-12px] w-[1px] bg-[#E5E5E5] pointer-events-none" />
            )}
            <RelationConnectorSvg isFirst={isFirst} hasParentError={isFirst && parentHasError} />

            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-start">
            <FormField
                type="select"
                label="Party Type"
                required
                disabled={isPrimaryBorrowerRow}
                placeholder="Select party type"
                value={relations.related_as || ""}
                options={rowPartyTypeOptions}
                hideLabel={!isFirst}
                error={show(`type:${relations.id}`) ? relationError : undefined}
                onChange={(v) =>
                    onChange({ related_as: v as RelatedAs })
                }
                onBlur={() => markTouched(`type:${relations.id}`)}
            />
            
            <div className="flex flex-col gap-1 min-w-0">
                {isFirst && <label className="text-xs font-medium text-gray-700">Loan Accounts</label>}
                <div className={`flex items-center border rounded-md bg-white min-h-9 px-2.5 ${
                        isPrimaryBorrowerRow ? "cursor-not-allowed opacity-90" : ""
                    } ${loanError ? "border-red-500" : "border-[#E5E5E5]"}`}
                >
                    <MultiSelectSummaryDropdown
                        name="accounts"
                        placeholder="Select accounts"
                        value={relations.account_numbers}
                        options={accountOptions}
                        disabled={isPrimaryBorrowerRow}
                        hideChevronWhenSelected={false}
                        onChange={(vals) => !isPrimaryBorrowerRow && onChange({ account_numbers: vals })}
                        renderCustomView={() => {
                            const selected = accountOptions.filter((o) =>
                                relations.account_numbers.includes(o.value)
                            );
                            if (selected.length === 0) {
                                return <span className="text-sm text-[#737373]">Select accounts</span>;
                            }
                            return (
                                <div className="flex flex-wrap gap-1">
                                    {selected.map((o) => (
                                        <span key={o.value}
                                        className="inline-flex items-center gap-1 rounded-md bg-[#EBEEFE] px-2 py-0.5 text-xs font-medium text-[#304ADD]">
                                        {o.name}
                                        {!isPrimaryBorrowerRow && (
                                            <button type="button" className="hover:text-red-500"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onChange({
                                                    account_numbers: relations.account_numbers.filter((v) => v !== o.value),
                                                });
                                            }}>
                                            <X width={12} height={12} />
                                            </button>
                                        )}
                                        </span>
                                    ))}
                                </div>
                            );
                        }}
                    />
                </div>
                {loanError && <span className="text-xs text-red-500">{loanError}</span>}
            </div>
            <div className={`flex items-center ${isFirst ? "pt-5" : ""}`}>
                <ButtonV2
                variant="outline"
                size="sm"
                disabled={isPrimaryBorrowerRow || allRelations.length <= 1}
                onClick={onDelete}
                title={allRelations.length <= 1 ? "At least one party type is required" : "Remove party type"}
                className="h-9 px-2 rounded-md bg-white border border-gray-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 disabled:opacity-40"
                prefix={<Trash2 width="16" height="16" />}
                />
            </div>
            </div>
            {isPrimaryBorrowerRow && (
                <div className="pointer-events-none absolute left-0 top-full z-50 mt-1 hidden w-max max-w-[400px] rounded-[6px] bg-[#1F2937] px-3 py-2 text-xs text-white shadow-lg group-hover:block">
                    Party is added as Primary Borrower in Loan accounts
                </div>
            )}
        </div>
    );
}